'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export interface TextSelectionState {
    text: string;
    rect: DOMRect | null;
}

const EMPTY: TextSelectionState = { text: '', rect: null };

/**
 * Tracks the user's text selection on the page.
 *
 * Excludes selections that originate inside elements matching
 * `[data-persistent-assistant]` so highlighting inside the chat panel
 * doesn't trigger the tooltip.
 */
export function useTextSelection() {
    const [selection, setSelection] = useState<TextSelectionState>(EMPTY);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const update = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
            setSelection(EMPTY);
            return;
        }

        const anchorNode = sel.anchorNode;
        if (
            (anchorNode && (anchorNode as HTMLElement).closest?.('[data-persistent-assistant]')) ||
            anchorNode?.parentElement?.closest('[data-persistent-assistant]')
        ) {
            setSelection(EMPTY);
            return;
        }

        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
            setSelection(EMPTY);
            return;
        }

        setSelection({ text: sel.toString().trim(), rect });
    }, []);

    const debouncedUpdate = useCallback(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(update, 150);
    }, [update]);

    const clear = useCallback(() => {
        clearTimeout(debounceRef.current);
        setSelection(EMPTY);
    }, []);

    useEffect(() => {
        document.addEventListener('selectionchange', debouncedUpdate);
        return () => {
            document.removeEventListener('selectionchange', debouncedUpdate);
            clearTimeout(debounceRef.current);
        };
    }, [debouncedUpdate]);

    return { selection, clearSelection: clear };
}
